import { Component } from "react"

/* Components */
import { Input } from "../../components"

/* Modal */
import Modal from "react-modal"

/* Redux */
import { connect } from "react-redux"

/* Translate */
import { t } from "../../translates/translate"

/* REST API */
import { edit } from "../../api/Card"

/* Constants */
import { env } from "../../constants"

/* Helpers */
import { utils } from "../../helpers"

/* Icons */
import { CloseIcon, InfoIcon, BarcodeIcon, WarningIcon, ClockIcon, HashIcon, ValueIcon, HeartIcon, DiamondIcon, ClubIcon, SpadeIcon } from "./CardIcons"

/* Utils */
import { getSuit, getSuitName, getRank, getCode, formatDate } from "./CardUtils"

/* Sweet alert */
import Swal from "../../helpers/alert"


/* EMPTY MODAL STATE */
const EMPTY = {
    id: 0,
    name: "",
    image: "",
    value: "",
    updatedAt: null,
    barcode: "",
    savedBarcode: "",
    waiting: false,
}

/* LENGTH OF THE BARCODE COLUMN — THE CONTROLLER KEEPS THE SAME NUMBER */
const BARCODE_LENGTH = 32

/* BARCODE OF A CARD — LETTERS, NUMBERS, A DASH, AN UNDERSCORE AND A DOT */
const BARCODE_PATTERN = /^[A-Za-z0-9._-]+$/


/* Widget Card Form */
class CardForm extends Component {

    constructor() {
        super()
        this.state = { visible: false, ...EMPTY }
    }

    /* Open Action — every value of the card is already loaded by the page */
    open = ({ item }) => {

        /* Fields */
        const barcode = getCode(item)

        this.setState({
            visible: true,
            ...EMPTY,
            id: parseInt(item.id),
            name: utils.checkValue(item.name, ""),
            image: utils.checkValue(item.image, ""),
            value: utils.checkValue(item.value, ""),
            updatedAt: item.updatedAt ? item.updatedAt : null,
            barcode,
            savedBarcode: barcode,
        })
    }

    /* Close Action */
    close = () => {
        this.setState({ visible: false, ...EMPTY })
    }

    /* Read The Error Message Of A Response */
    message = (response) => {

        if (response && response.data && response.data.message) {
            return response.data.message
        }

        if (response && response.status === 498) {
            return t("swal.invalidKey")
        }

        if (response && (response.status === 401 || response.status === 403)) {
            return t("swal.noAccess")
        }

        return t("swal.generic")
    }

    /* Owner Of A Barcode — the whole deck is already in the page, so a conflict is visible before saving */
    getOwner = () => {

        /* Fields */
        const { id, barcode } = this.state
        const { deck } = this.props
        const value = barcode.trim().toUpperCase()

        /* Check Value */
        if (value === "" || !deck) {
            return null
        }

        /* Send Result */
        const owner = deck.find((item) => parseInt(item.id) !== id && getCode(item).toUpperCase() === value)

        return owner ? owner : null
    }

    /* Save Action */
    save = () => {

        /* Fields */
        const { id, barcode, savedBarcode } = this.state
        const value = barcode.trim()

        /* Check Barcode */
        if (value === "") {
            this._barcode.error(t("card.error.emptyBarcode"))
            return
        }

        /* Check Barcode Format */
        if (!BARCODE_PATTERN.test(value)) {
            this._barcode.error(t("card.error.formatBarcode"))
            return
        }

        /* Check Owner — the same code on two cards makes every scan ambiguous */
        const owner = this.getOwner()

        if (owner) {
            this._barcode.error(t("card.error.busyBarcode"))
            return
        }

        /* Check Changes */
        if (value === savedBarcode) {
            this.close()
            return
        }

        /* Start Waiting */
        this.setState({ waiting: true })

        /* Send to REST API */
        edit({ id, barcode: value }).then((response) => {
            if (response.status === 200) {
                this.finish()
            }
            else {
                Swal.fire(utils.notification(this.message(response), "error"))
                this.setState({ waiting: false })
            }
        }).catch(() => {
            Swal.fire(utils.notification(t("swal.generic"), "error"))
            this.setState({ waiting: false })
        })

    }

    /* Finish Action */
    finish = () => {

        /* Fields */
        const { reload } = this.props

        Swal.fire(utils.notification(t("card.savedSuccess"), "success"))
        reload()
        this.close()
    }

    /* Draw Suit Icon */
    _suit = (suit, size) => {

        /* Check Suit */
        if (suit === "heart") {
            return <HeartIcon size={size} />
        }

        if (suit === "diamond") {
            return <DiamondIcon size={size} />
        }

        if (suit === "club") {
            return <ClubIcon size={size} />
        }

        /* Send Result */
        return <SpadeIcon size={size} />
    }

    /* Draw Preview — the card itself, so the code goes to the right picture */
    _preview = () => {

        /* Fields */
        const { id, name, image, value, updatedAt, savedBarcode } = this.state
        const suit = getSuit({ name, image })

        return (
            <div className="card--preview">

                <div className={`card--preview-image ${suit}`}>
                    <img src={`${env.mediapoint}cards/${image}`} alt={name} />
                </div>

                <div className="card--preview-text">

                    <div className="card--preview-name">
                        <span className={`card--preview-rank ${suit}`}>{getRank({ value })}</span>
                        <span className={`card--preview-suit ${suit}`}>{this._suit(suit, 16)}</span>
                        {name}
                    </div>

                    <div className="card--preview-note">

                        <span>
                            <HashIcon size={12} />
                            {id}
                        </span>

                        <span>
                            <ValueIcon size={12} />
                            {t("card.value")}: {utils.checkValue(value, "-")}
                        </span>

                        <span>
                            <BarcodeIcon size={12} />
                            {savedBarcode !== "" ? savedBarcode : t("card.noBarcode")}
                        </span>

                        <span>
                            <ClockIcon size={12} />
                            {formatDate(updatedAt)}
                        </span>

                    </div>

                </div>

            </div>
        )
    }

    /* Draw Barcode Section — the only editable column of the deck */
    _barcodeSection = () => {

        /* Fields */
        const { barcode } = this.state
        const owner = this.getOwner()

        return (
            <div className="card--section">

                <div className="card--section-head">
                    <div className="card--section-title">
                        <BarcodeIcon size={13} />
                        {t("card.barcode")}
                    </div>
                </div>

                <Input
                    ref={(ref) => this._barcode = ref}
                    label={t("card.barcode")}
                    value={barcode}
                    maxLength={BARCODE_LENGTH}
                    onChange={(event) => this.setState({ barcode: event.target.value })}
                />

                {/* The code is already on another card — the scan would become ambiguous */}
                {owner
                    ? <div className="card--hint red">
                        <WarningIcon size={16} />
                        <span>{t("card.busyHint")} {owner.name}</span>
                    </div>
                    : <div className="card--hint">
                        <InfoIcon />
                        <span>{t("card.barcodeHint")}</span>
                    </div>
                }

            </div>
        )
    }

    render = () => {

        /* Fields */
        const { visible, name, image, value, waiting } = this.state
        const suit = getSuit({ name, image })

        return (
            <Modal
                isOpen={visible}
                onRequestClose={() => this.close()}
                className="card--modal"
                overlayClassName="card--overlay"
                closeTimeoutMS={200}
            >

                <div className="card--modal-header">

                    <div className={`card--modal-icon ${suit}`}>
                        {this._suit(suit, 18)}
                    </div>

                    <div className="card--modal-head-text">
                        <h2>{t("card.editTitle")}</h2>
                        <p>{name !== "" ? `${getRank({ value })} · ${getSuitName(suit)} · ${name}` : t("card.title")}</p>
                    </div>

                    <div onClick={() => this.close()} className="card--modal-close">
                        <CloseIcon />
                    </div>

                </div>

                <div className="card--modal-body">

                    {/* Draw Preview */}
                    {this._preview()}

                    {/* Draw Barcode Section */}
                    {this._barcodeSection()}

                </div>

                <div className="card--modal-footer">

                    <div onClick={() => this.close()} className="card--button ghost">{t("common.cancel")}</div>

                    {waiting
                        ? <div className="card--button primary"><div className="card--loading small" /></div>
                        : <div onClick={() => this.save()} className="card--button primary">{t("common.save")}</div>
                    }

                </div>

            </Modal>
        )
    }

}

const mapStateToProps = (state) => ({
    language: state.language,
})

export default connect(mapStateToProps, null, null, { forwardRef: true })(CardForm)
